<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { IDestinationSelection } from '@/stores/printing-v2.config';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import V2PrinterAssignmentPicker from '../V2PrinterAssignmentPicker.vue';

  export interface IWizardAssignResult {
    name: string;
    selection: IDestinationSelection[];
  }

  const props = withDefaults(
    defineProps<{
      defaultName: string;
      /** Pre-checked destination (unresolved-v1 reassign flow). */
      preSelection?: IDestinationSelection[];
      finishing?: boolean;
    }>(),
    { preSelection: () => [], finishing: false }
  );

  const emit = defineEmits<{
    (e: 'finish', result: IWizardAssignResult): void;
  }>();

  const name = ref(props.defaultName);
  const selection = ref<IDestinationSelection[]>([...props.preSelection]);

  const canFinish = computed(() => name.value.trim().length > 0);

  function finish() {
    if (!canFinish.value) return;
    emit('finish', { name: name.value.trim(), selection: selection.value });
  }
</script>

<template>
  <div class="wizard-step-assign">
    <div class="assign-field">
      <label class="assign-label">{{ $t('printingV2.assignName') }}</label>
      <PosUiInput
        :value="name"
        :maxlength="40"
        class="assign-name-input"
        @input="name = $event"
      />
    </div>

    <div class="assign-field">
      <label class="assign-label">{{ $t('printingV2.assignQuestion') }}</label>
      <V2PrinterAssignmentPicker
        :value="selection"
        @input="selection = $event"
      />
    </div>

    <div class="assign-footer">
      <PosUiButton
        color="primary"
        size="lg"
        icon="check"
        :loading="finishing"
        :disabled="!canFinish"
        @click="finish"
      >
        {{ $t('printingV2.finish') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .wizard-step-assign {
    @apply tw-flex tw-flex-col tw-gap-4;

    .assign-field {
      @apply tw-flex tw-flex-col tw-gap-1.5;

      .assign-label {
        @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }

      .assign-name-input {
        @apply tw-max-w-sm;
      }
    }

    .assign-footer {
      @apply tw-flex tw-justify-end tw-pt-2;
    }
  }
</style>
